/**
 * @file CustomFieldInput.js
 * @description Shared input component for custom fields, supporting text, money, and date types.
 * Accepts an optional styles prop (CSS module object) for host-specific label/input class overrides.
 */

import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import MoneyInput from './specialFormatInputs/MoneyInput';

/**
 * Renders a single custom field input (text, money, or date).
 * @param {Object} props
 * @param {{ fieldName: string|number, value: * }} props.field - Field descriptor with identifier and current value.
 * @param {string} props.fieldName - Display label for the field.
 * @param {Function} props.handleInputChange - Callback invoked with (fieldName, value) on change.
 * @param {Function} [props.assignRef] - Optional ref assignment callback for the label element.
 * @param {boolean} props.isDateField - Whether the field is a date picker.
 * @param {boolean} props.isMoneyField - Whether the field is a money input.
 * @param {boolean} props.isTextField - Whether the field is a plain text input.
 * @param {string} [props.subLabel] - Optional secondary label shown below the main label.
 * @param {Object} [props.styles] - Optional CSS module object with labelContainer, inputLabel, and inputField keys.
 * @param {boolean} [props.inlineLabel=true] - When true, label renders inline (side-by-side) with the input. When false, label renders above the input.
 * @param {string} [props.errorMessage] - Validation error message to display beneath the input.
 * @param {boolean} [props.required=false] - When true, appends a required asterisk to the label.
 */
const CustomFieldInput = ({
    field,
    fieldName,
    handleInputChange,
    assignRef,
    isDateField,
    isMoneyField,
    isTextField,
    subLabel = '',
    styles = {},
    inlineLabel = true,
    errorMessage,
    required = false,
}) => (
    <div
        className={`form-group ${
            inlineLabel ? 'inline-input flex-sm-wrap flex-lg-nowrap' : ''
        } ${!isMoneyField ? 'mb-3' : ''}`}
    >
        {!isMoneyField && inlineLabel && (
            <div className={styles.labelContainer || ''}>
                <label
                    htmlFor={`${fieldName}`}
                    ref={assignRef}
                    className={`${styles.inputLabel || ''} ${errorMessage ? 'text-danger' : ''}`}
                    style={{
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                        textTransform: 'capitalize',
                    }}
                >
                    {fieldName}
                    {required && <span className="text-danger">*</span>}
                    <br />
                    {subLabel && (
                        <div style={{ fontSize: '14px' }}>{subLabel}</div>
                    )}
                </label>
            </div>
        )}

        {!isMoneyField && !inlineLabel && (
            <label
                htmlFor={`${fieldName}`}
                ref={assignRef}
                className={`${styles.inputLabel || ''} ${errorMessage ? 'text-danger' : ''}`}
                style={{ textTransform: 'capitalize' }}
            >
                {fieldName}
                {required && <span className="text-danger">*</span>}
                {subLabel && (
                    <span style={{ fontSize: '14px', marginLeft: '4px' }}>
                        {subLabel}
                    </span>
                )}
            </label>
        )}

        {isDateField && (
            <CustomDatePicker
                selectedDate={field.value}
                onDateChange={date => handleInputChange(field.fieldName, date)}
                errorMessage={errorMessage}
                required={required}
            />
        )}

        {isMoneyField && (
            <MoneyInput
                name={`${fieldName}`}
                label={`${fieldName}`}
                value={field.value}
                onChange={value => handleInputChange(field.fieldName, value)}
                inlineLabel={inlineLabel}
                isRequired={required}
                errorMessage={errorMessage}
                hideErrorMessage={false}
                className={styles.inputField || ''}
                subLabel={subLabel}
            />
        )}

        {isTextField && (
            <>
                <input
                    type="text"
                    maxLength="128"
                    className={`form-control ${styles.inputField || ''} ${errorMessage ? 'is-invalid' : ''}`}
                    value={field.value || ''}
                    onChange={e =>
                        handleInputChange(field.fieldName, e.target.value)
                    }
                />
                {errorMessage && (
                    <div className="invalid-feedback">{errorMessage}</div>
                )}
            </>
        )}
    </div>
);

export default CustomFieldInput;
